import React from 'react';
import { Box, useTheme, alpha, Typography } from '@mui/material';

/**
 * TechBackground Component
 * Ultra-modern Cyberpunk / Synthwave Ambient Background
 * Features:
 * - Dynamic glowing ambient orbs (Orange #FF6B35, Cyan #00D4FF, Purple #8B5CF6)
 * - Animated retro-futuristic perspective soundwave grid
 * - Subtle digital scanlines and floating audio visualizer pulses
 */
const TechBackground = () => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main; // #FF6B35
  const secondaryColor = theme.palette.secondary.main; // #00D4FF
  const accentColor = theme.palette.accent?.main || '#8B5CF6';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1,
        overflow: 'hidden',
        transform: 'translateZ(0)',
      }}
    >
      {/* 1. Ambient Glowing Neon Orbs (Atmospheric Depth) */}
      <Box
        sx={{
          position: 'absolute',
          top: '-15%',
          left: '10%',
          width: { xs: '320px', md: '550px' },
          height: { xs: '320px', md: '550px' },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(primaryColor, 0.22)} 0%, ${alpha(primaryColor, 0.08)} 45%, transparent 70%)`,
          filter: { xs: 'none', md: 'blur(80px)' },
          transform: 'translateZ(0)',
          willChange: 'transform',
          animation: 'floatOrb1 18s ease-in-out infinite alternate',
          '@keyframes floatOrb1': {
            '0%': { transform: 'translate(0, 0) scale(1)' },
            '50%': { transform: 'translate(40px, 30px) scale(1.1)' },
            '100%': { transform: 'translate(-30px, 15px) scale(0.95)' },
          },
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '25%',
          right: '5%',
          width: { xs: '340px', md: '600px' },
          height: { xs: '340px', md: '600px' },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(secondaryColor, 0.18)} 0%, ${alpha(secondaryColor, 0.06)} 45%, transparent 70%)`,
          filter: { xs: 'none', md: 'blur(90px)' },
          transform: 'translateZ(0)',
          willChange: 'transform',
          animation: 'floatOrb2 22s ease-in-out infinite alternate',
          '@keyframes floatOrb2': {
            '0%': { transform: 'translate(0, 0) scale(1)' },
            '50%': { transform: 'translate(-40px, 45px) scale(1.08)' },
            '100%': { transform: 'translate(25px, -20px) scale(0.92)' },
          },
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '30%',
          width: { xs: '300px', md: '500px' },
          height: { xs: '300px', md: '500px' },
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(accentColor, 0.16)} 0%, ${alpha(accentColor, 0.05)} 45%, transparent 70%)`,
          filter: { xs: 'none', md: 'blur(80px)' },
          transform: 'translateZ(0)',
          willChange: 'transform',
          animation: 'floatOrb3 20s ease-in-out infinite alternate',
          '@keyframes floatOrb3': {
            '0%': { transform: 'translate(0, 0) scale(0.95)' },
            '50%': { transform: 'translate(30px, -30px) scale(1.08)' },
            '100%': { transform: 'translate(-20px, 20px) scale(1)' },
          },
        }}
      />

      {/* 2. Cyberpunk Synthwave Grid (Perspective Floor / Ceiling Grid) */}
      <Box
        sx={{
          position: 'absolute',
          top: '64px',
          left: 0,
          right: 0,
          height: '420px',
          backgroundImage: `
            linear-gradient(to right, ${alpha(secondaryColor, 0.04)} 1px, transparent 1px),
            linear-gradient(to bottom, ${alpha(primaryColor, 0.04)} 1px, transparent 1px)
          `,
          backgroundSize: '48px 48px',
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 60%, transparent 100%)',
        }}
      />

      {/* 3. Sleek Animated Equalizer Skyline Bars */}
      <Box
        sx={{
          position: 'absolute',
          top: '70px',
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: { xs: 1.5, md: 3 },
          opacity: 0.35,
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, transparent 90%)',
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, transparent 90%)',
        }}
      >
        {Array.from({ length: 48 }).map((_, i) => {
          const isOrange = i % 3 === 0;
          const isCyan = i % 3 === 1;
          const barColor = isOrange ? primaryColor : isCyan ? secondaryColor : accentColor;
          const duration = 1.2 + (i % 5) * 0.3;
          const delay = (i % 7) * 0.15;

          return (
            <Box
              key={i}
              sx={{
                width: { xs: '4px', md: '6px' },
                height: `${25 + ((i * 17) % 55)}px`,
                borderRadius: '3px',
                background: `linear-gradient(to bottom, ${barColor}, transparent)`,
                boxShadow: { xs: 'none', md: `0 0 6px ${alpha(barColor, 0.5)}` },
                display: { xs: i >= 20 ? 'none' : 'block', sm: i >= 32 ? 'none' : 'block', md: 'block' },
                animation: `pulseBar ${duration}s ease-in-out infinite alternate`,
                animationDelay: `${delay}s`,
                '@keyframes pulseBar': {
                  '0%': { transform: 'scaleY(0.4)', opacity: 0.3 },
                  '50%': { transform: 'scaleY(1.15)', opacity: 0.9 },
                  '100%': { transform: 'scaleY(0.6)', opacity: 0.5 },
                },
              }}
            />
          );
        })}
      </Box>

      {/* 4. Elegant Typography Watermark */}
      <Typography
        variant="h1"
        sx={{
          position: 'absolute',
          top: '130px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: { xs: '3.5rem', sm: '6rem', md: '8.5rem' },
          fontWeight: 900,
          color: 'transparent',
          WebkitTextStroke: `1px ${alpha(primaryColor, 0.08)}`,
          textTransform: 'uppercase',
          userSelect: 'none',
          letterSpacing: '0.4rem',
          fontFamily: '"Orbitron", sans-serif',
          whiteSpace: 'nowrap',
          lineHeight: 1,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        NOSTALBEAT
      </Typography>

      {/* 5. Subtle CRT Scanline Texture (Cyberpunk Vibe) - Enabled on Desktop */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.12) 0px,
            rgba(0, 0, 0, 0.12) 1px,
            transparent 1px,
            transparent 3px
          )`,
          opacity: 0.6,
          display: { xs: 'none', md: 'block' },
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default TechBackground;
